import { Component } from 'vue-property-decorator';
import AbstractDisplayField from '../abstract-display-field/abstract-display-field';
import { mixins } from 'vue-class-component';
import BpcDrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import ContactForm from '@contacts/components/forms/contact-form/contact-form.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';

import moment from 'moment';
import AccessControlBadgeForm from '@access-control/components/access-control-badge-form.vue';
import AuthorizationHelper from '@auth/services/authorization.helper';
import ModulesHelper from '@core/services/modules.helper';
import { AccessControlBadge } from '@access-control/interfaces/access-control-badge';
import MergeContactsModal from '@contacts/components/merge-contacts/merge-contacts-modal.vue';

library.add(faFlag);
@Component({
  name: 'bpc-display-contact-link',
  components: {
    MergeContactsModal,
    'bpc-drawer-modal': BpcDrawerModal,
    'bpc-contact-form': ContactForm,
    'bpc-std-form': StdForm,
    'bpc-access-control-badge-form': AccessControlBadgeForm,
  },
})
export default class DisplayContactLink extends mixins(AbstractDisplayField) {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    stdForm: InstanceType<typeof StdForm> & {
      saveEntity(): void;
      formModel: any;
      onDeleteMediaFile(): void;
      searchEntity(): void;
      extraData: string | AnyEntity;
      formProvider: EntityFormProvider;
    };
  };

  public entity: any = null;

  public showForm: boolean = false;
  showMergeContactsModal: boolean = false;
  mergeContactsButtonLabel: string = 'global.merge_contacts_button_label';
  isMergingContacts: boolean = false;
  public showDrawerSection: boolean = false;

  public isSavingForm = false;

  public disabledForm = true; // For read only mode when click in code column
  public permissionEntityNameToUse = 'contacts';

  public contact = null;

  beforeMount() {
    const sourcePath = this.fieldConfig?.['source'] ?? 'contact';
    const contactValue = this.getNestedValue(this.item, sourcePath) ?? this.item;
    this.contact = contactValue;
  }

  getNestedValue(obj, path) {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  get size(): string {
    return this.fieldConfig?.drawerSize || 'md';
  }

  public onSavingEntity(val) {
    this.isSavingForm = val;
  }

  get commentData() {
    return this.contact['comment'];
  }

  get toInvoice() {
    return this.contact['to_invoice'];
  }

  get isVerified() {
    return this.contact['verified'] ?? true;
  }

  get tags() {
    const tags = this.item['contact_tags'] ? this.item['contact_tags'] : [];
    return !tags ? this.contact['contact_tags'] : tags;
  }

  get code() {
    return this.item['code'];
  }

  get isVerifiedText() {
    if (this.isVerified) {
      return this.$t('contacts.contacts.verified');
    } else {
      return this.$t('contacts.contacts.not_verified');
    }
  }

  get whenToInvoice() {
    const whenToInvoice = this.contact['when_to_invoice_value'] ?? this.contact['when_to_invoice'];
    switch (whenToInvoice) {
      case 'before':
        return this.$t('contacts.contacts.when_to_invoice.tooltip.before');
      case 'after':
        return this.$t('contacts.contacts.when_to_invoice.tooltip.after');
      case 'undefined':
        return this.$t('contacts.contacts.when_to_invoice.tooltip.undefined');
      default:
        return '';
    }
  }

  clickOnLink() {
    this.entity = this.contact;
    this.showForm = true;
    this.showDrawerSection = true;
    this.disabledForm = true;
  }

  clickOnMultiLink(contact) {
    this.entity = contact;
    this.showForm = true;
    this.showDrawerSection = true;
    this.disabledForm = true;
  }

  onFormSave() {
    this.isSavingForm = true;
    console.log('Saving');
    this.$refs.stdForm.saveEntity();
  }

  onFormSaved($event: any = null) {
    this.onFormClose();
    this.$emit('entity-saved');
  }

  onFormClose() {
    this.showForm = false;
    this.showDrawerSection = false;
  }

  get entityName(): string {
    return 'contact';
  }

  get formComponentName(): string {
    return 'bpc-contact-form';
  }

  onFormEdit() {
    this.disabledForm = false;
  }

  getKey() {
    return uuidv4();
  }

  getModalTitle() {
    if (this.disabledForm) {
      const title = this.$t('global.models.' + this.entityName + '.name').toString();
      return title.charAt(0).toUpperCase() + title.slice(1); // capitalize
    } else if (this.entity && this.entity.id) {
      return (
        this.$t('global.modals.edit') + ' ' + this.$t('global.models.' + this.entityName + '.name')
      );
    } else {
      return (
        this.$t('global.modals.create') +
        ' ' +
        this.$t('global.models.' + this.entityName + '.name')
      );
    }
  }

  /**
   * For the action to add an access control badge
   */

  timestamp = moment().unix();

  get hasAccessControlModuleActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ACCESS_CONTROL_MODULE_NAME);
  }

  get canCreateAccessControlBadges() {
    const permissionName = AuthorizationHelper.getPermissionName('store', 'access_control_badges');
    return this.$authorizationService.hasPermissions([permissionName]);
  }

  get initialAccessControlBadgeData(): Partial<AccessControlBadge> {
    return { contact_id: this.contact.id, status: 'active', type: 'rfid' };
  }

  isSavingAccessControlBadge: boolean = false;
  labelSaveAccessControlBadgeButton: string =
    'global.access_control_badge.label_to_add_from_contact';

  get accessControlBadgeFormCancelPortalTarget() {
    return `access-control-badge-form-cancel-action-${this.timestamp}`;
  }

  get accessControlBadgeFormSavePortalTarget() {
    return `access-control-badge-form-actions-${this.timestamp}`;
  }

  get addAccessControlBadgeModalId(): string {
    const rand = Math.floor(Math.random() * 10000000);
    return this.contact
      ? `modal-add-access-control-badge-${this.contact.id}-${rand}`
      : `modal-add-access-control-badge-${rand}`;
  }

  get titleAddAccessControlBadgeModal(): string {
    return (
      this.$t('global.modals.create') + ' ' + this.$t('global.models.access-control-badge.name')
    );
  }
  onClickCreateAccessControlBadge() {
    this.$bvModal.show(this.addAccessControlBadgeModalId);
  }

  onCloseAccessControlBadgeModal() {
    this.$bvModal.hide(this.addAccessControlBadgeModalId);
  }

  openMergeContactsModal(): void {
    this.showMergeContactsModal = true;
  }
  closeMergeContactsModal(): void {
    this.showMergeContactsModal = false;
  }

  onContactsMerged() {
    // this.i++;
    // this.$refs.provider.getEntities();
  }
}
